import { InfoBox } from "@/components/ui/info-box";
import { SearchInput } from "@/components/ui/search-input";
import { TabButton } from "@/components/ui/tab-button";
import type { SearchType } from "@/lib/types";

interface SearchInterfaceProps {
  searchType: SearchType;
  setSearchType: (type: SearchType) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  onSearch: () => void;
}

export function SearchInterface({
  searchType,
  setSearchType,
  searchQuery,
  setSearchQuery,
  onSearch,
}: SearchInterfaceProps) {
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      onSearch();
    }
  };

  const getLabel = () => {
    if (searchType === "case") return "Public Case Number";
    if (searchType === "domain") return "Domain Name";
    return "Company Name";
  };

  const getPlaceholder = () => {
    if (searchType === "case") return "e.g., NCVIC-2024-001234";
    if (searchType === "domain") return "e.g., example.com";
    return "e.g., Cloudflare";
  };

  const getDescription = () => {
    if (searchType === "case") {
      return "Enter a public case number to view the takedown notice and reasons for content removal.";
    }
    if (searchType === "domain") {
      return "Enter a domain name to see if it has been seized or taken down, including the reason and responsible provider.";
    }
    return "Enter a company name to view what domains have been blocked and why the company made that decision.";
  };

  return (
    <div className="space-y-6">
      <div className="bg-gray-50 rounded-lg p-4 sm:p-6 border border-gray-200">
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-4 mb-6">
          <TabButton
            active={searchType === "case"}
            onClick={() => setSearchType("case")}
          >
            Search by Case Number
          </TabButton>
          <TabButton
            active={searchType === "domain"}
            onClick={() => setSearchType("domain")}
          >
            Search by Domain
          </TabButton>
          <TabButton
            active={searchType === "company"}
            onClick={() => setSearchType("company")}
          >
            Search by Company
          </TabButton>
        </div>

        <div className="space-y-4">
          <div>
            <label
              htmlFor="search"
              className="block mb-2 text-gray-700 text-sm sm:text-base"
            >
              {getLabel()}
            </label>
            <SearchInput
              id="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={getPlaceholder()}
              onSearchClick={onSearch}
              showButton
            />
          </div>

          <div className="text-sm text-gray-600">
            <p>{getDescription()}</p>
          </div>
        </div>
      </div>

      <InfoBox variant="info">
        <p className="text-sm">
          <strong>Try these examples:</strong>
        </p>
        <ul className="text-sm mt-2 space-y-1">
          <li>• Case Number: NCVIC-2024-001234</li>
          <li>• Domain: illegalcontent.com</li>
          <li>• Company: Cloudflare</li>
        </ul>
      </InfoBox>
    </div>
  );
}
